'use client';

import { useEffect, useState } from 'react';
import { useRegisterServiceWorker } from '@/hooks';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import Salesforce, { SalesforceProps } from './Salesforce';

interface ClientPageHeadProps  {
    envName: string;
    ddenv: string;
    launchScript: string;
    onetrust: {
        script: string;
        domainScript: string;
    };
    pageInfo: {
      analyticsPageName: string;
      analyticsPageType: string;
      pageType: PageType;
    };
    abTest?: SalesforceProps;
}

export default function ClientPageHead(props:ClientPageHeadProps): React.JSX.Element {
    const [ isAnalyticsReady, setIsAnalyticsReady ] = useState<boolean>(false);
    const [ddLoaded, setDdLoaded] = useState<boolean>(false);
    const searchParams = useSearchParams();

    useRegisterServiceWorker('/service-worker.js');

    useEffect(() => {
        window.dispatchEvent(new Event('hydrate'));
    }, []);

    useEffect(() => {
        const paramStr = searchParams.toString();
        if (paramStr) {
            try {
                window.sessionStorage.setItem('paramStr', paramStr);
            } catch(e) {
                console.error(e);
            }
        }
    }, [searchParams]);

    useEffect(() => {
        const browserWindow = window as BrowserWindow;

        if (browserWindow.DD_RUM && (process.env.NODE_ENV === 'production')) {
            browserWindow.DD_RUM.init({
                clientToken: 'pub2efebd86de973d3161e20023ac07e68a',
                applicationId: 'c27edd08-fd30-4d05-ace8-21ad46980f08',
                site: 'datadoghq.com',
                service: 'sxm-cecm-web-www',
                env: props.ddenv,
                version: process.env.NEXT_PUBLIC_DD_VERSION || '1.0.0',
                sessionSampleRate: 45,
                sessionReplaySampleRate: 20,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'mask-user-input',
            });

            browserWindow.DD_RUM.startSessionReplayRecording();
            browserWindow.DD_RUM.setGlobalContext(props.pageInfo);
        }
    }, [ddLoaded, props.ddenv, props.pageInfo]);

    return (
        <>
            <Script
                src="https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js"
                onLoad={() => {
                    setDdLoaded(true);
                }}
            />

            <Script
                src={props.launchScript}
                async
                strategy='afterInteractive'
                onLoad={() => {
                    const script = document.createElement('script');
                    script.setAttribute('src', '/resources/js/analytics.js');
                    script.onload = () => setIsAnalyticsReady(true);
                    document.body.appendChild(script);
                }}
            />

            {process.env.NODE_ENV !== 'development' && (
                <Script
                    src={props.onetrust.script}
                    strategy='afterInteractive'
                    data-domain-script={props.onetrust.domainScript}
                    onLoad={() => {
                        const browserWindow = window as BrowserWindow;
                        browserWindow.OptanonWrapper = () => {
                            browserWindow.DD_RUM?.setGlobalContextProperty('consent', browserWindow.OnetrustActiveGroups);
                        }
                    }}
                />
            )}

            {
                isAnalyticsReady && props.abTest &&
                <Salesforce {...props.abTest} />
            }
        </>
    );
}
